// src/components/currentaffairs/Daily.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Download, ExternalLink, Calendar, Search, RefreshCw } from "lucide-react";
import { publicAPI, resolvePublicAssetUrl } from "../../services/publicApi";
import { currentAffairsData } from "../../data/staticData";

export default function Daily() {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [dailySearch, setDailySearch] = useState("");

  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [monthlySearch, setMonthlySearch] = useState("");

  const [dailyPage, setDailyPage] = useState(1);
  const [monthlyPage, setMonthlyPage] = useState(1);
  const itemsPerPage = 6;

  // Backend dynamic state
  const [backendDaily, setBackendDaily] = useState([]);
  const [backendMonthly, setBackendMonthly] = useState([]);
  const [isLoadingDaily, setIsLoadingDaily] = useState(false);
  const [isLoadingMonthly, setIsLoadingMonthly] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // Fetch published Daily CA from backend
  const fetchPublishedDaily = useCallback(async (isBackground = false) => {
    if (!isBackground) setIsLoadingDaily(true);
    try {
      const res = await publicAPI.getDailyCA();
      if (res?.success && Array.isArray(res.data)) {
        setBackendDaily(res.data);
        setLastSynced(new Date());
      }
    } catch (err) {
      console.warn("Backend Daily CA not reachable, using fallback static data.", err.message);
    } finally {
      if (!isBackground) setIsLoadingDaily(false);
    }
  }, []);

  // Fetch published Monthly CA from backend
  const fetchPublishedMonthly = useCallback(async (isBackground = false) => {
    if (!isBackground) setIsLoadingMonthly(true);
    try {
      const res = await publicAPI.getMonthlyCA();
      if (res?.success && Array.isArray(res.data)) {
        setBackendMonthly(res.data);
        setLastSynced(new Date());
      }
    } catch (err) {
      console.warn("Backend Monthly CA not reachable, using fallback static data.", err.message);
    } finally {
      if (!isBackground) setIsLoadingMonthly(false);
    }
  }, []);

  // Initial fetch and automatic background synchronization (polling every 12 seconds)
  useEffect(() => {
    fetchPublishedDaily();
    fetchPublishedMonthly();

    const interval = setInterval(() => {
      fetchPublishedDaily(true);
      fetchPublishedMonthly(true);
    }, 12000);

    const handleFocus = () => {
      fetchPublishedDaily(true);
      fetchPublishedMonthly(true);
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) handleFocus();
    });

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchPublishedDaily, fetchPublishedMonthly]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchPublishedDaily(), fetchPublishedMonthly()]);
    setIsRefreshing(false);
  };

  // Combined daily list: prioritize backend items, fallback to static items if backend is empty
  const allDailyItems =
    backendDaily.length > 0
      ? backendDaily
      : currentAffairsData.filter((i) => i.filterType === "Daily");

  // Filter daily items
  const currentDaily = allDailyItems.filter((item) => {
    const itemDate = item.date
      ? new Date(item.date).toISOString().split("T")[0]
      : item.publicationDate
      ? new Date(item.publicationDate).toISOString().split("T")[0]
      : item.createdAt
      ? new Date(item.createdAt).toISOString().split("T")[0]
      : "";

    const dateMatches = !selectedDate || itemDate === selectedDate;
    const categoryMatches =
      selectedCategory === "All" ||
      (item.category && item.category.toLowerCase() === selectedCategory.toLowerCase());

    const searchMatches =
      !dailySearch.trim() ||
      (item.title && item.title.toLowerCase().includes(dailySearch.toLowerCase())) ||
      (item.description && item.description.toLowerCase().includes(dailySearch.toLowerCase()));

    return dateMatches && categoryMatches && searchMatches;
  });

  // Combined monthly list: prioritize backend items, fallback to static items if backend is empty
  const allMonthlyItems =
    backendMonthly.length > 0
      ? backendMonthly
      : currentAffairsData.filter((i) => i.filterType === "Monthly");

  // Filter monthly items
  const currentMonthly = allMonthlyItems.filter((item) => {
    const mMatch =
      selectedMonth === "All" ||
      (item.month || "").toLowerCase() === selectedMonth.toLowerCase();
    const yMatch =
      !selectedYear ||
      selectedYear === "All" ||
      String(item.year || "") === String(selectedYear);

    const searchMatches =
      !monthlySearch.trim() ||
      (item.title && item.title.toLowerCase().includes(monthlySearch.toLowerCase())) ||
      (item.description && item.description.toLowerCase().includes(monthlySearch.toLowerCase()));

    return mMatch && yMatch && searchMatches;
  });

  // Pagination logic
  const paginate = (data, page) => {
    const start = (page - 1) * itemsPerPage;
    return data.slice(start, start + itemsPerPage);
  };

  const totalDailyPages = Math.ceil(currentDaily.length / itemsPerPage);
  const totalMonthlyPages = Math.ceil(currentMonthly.length / itemsPerPage);

  const handleOpenFile = (url) => {
    const targetUrl = resolvePublicAssetUrl(url);
    if (!targetUrl) {
      alert("Document not available.");
      return;
    }
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="relative w-full px-4 py-10 bg-gray-50 space-y-12">
      <div className="max-w-6xl mx-auto space-y-10">
        {/* Banner */}
        <div className="bg-gradient-to-r from-[#7b0000] via-red-900 to-red-950 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-amber-200 bg-white/10 px-3 py-1 rounded-full">
                  Free Initiative
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300 font-semibold bg-emerald-950/60 border border-emerald-500/30 px-3 py-1 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  Live Sync Active {lastSynced ? `• Synced ${lastSynced.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                Current Affairs
              </h1>
              <p className="text-white/80 mt-2 text-sm md:text-base max-w-2xl leading-relaxed">
                Daily news summaries, PIB briefs, and monthly consolidated digests synchronized in real-time from the Selva's IAS Academy faculty dashboard.
              </p>
            </div>

            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="self-start md:self-center inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/15 hover:bg-white/25 text-white text-xs font-bold transition-all border border-white/20 active:scale-95 disabled:opacity-50"
              title="Sync latest updates from Academy database"
            >
              <RefreshCw size={14} className={isRefreshing ? "animate-spin" : ""} />
              <span>{isRefreshing ? "Syncing..." : "Sync Updates"}</span>
            </button>
          </div>
        </div>

        {/* ================= 1. DAILY CURRENT AFFAIRS SECTION ================= */}
        <div id="daily" className="scroll-mt-28 bg-white rounded-2xl shadow-md p-6 sm:p-8 border-t-4 border-red-800">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 pb-5 border-b border-slate-100">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-red-900">
                  Daily Current Affairs
                </h2>
                <span className="text-xs font-bold bg-red-50 text-red-800 px-2.5 py-0.5 rounded-full border border-red-200">
                  {currentDaily.length} {currentDaily.length === 1 ? "Update" : "Updates"}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Curated daily analytical notes categorized by GS syllabus papers. Latest uploads appear first.
              </p>
            </div>

            {/* Filter controls */}
            <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setDailyPage(1);
                }}
                className="text-xs border border-slate-200 bg-slate-50 hover:bg-white rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
              >
                <option value="All">All Categories</option>
                <option value="Polity">Polity</option>
                <option value="Economy">Economy</option>
                <option value="Environment">Environment</option>
                <option value="Science & Tech">Science & Tech</option>
                <option value="International Relations">International Relations</option>
                <option value="National">National</option>
                <option value="Art & Culture">Art & Culture</option>
                <option value="General">General</option>
              </select>

              {/* Date Filter */}
              <div className="flex items-center gap-1.5">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setDailyPage(1);
                  }}
                  className="text-xs border border-slate-200 bg-slate-50 hover:bg-white rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900"
                />
                {selectedDate && (
                  <button
                    onClick={() => {
                      setSelectedDate("");
                      setDailyPage(1);
                    }}
                    className="text-[11px] text-red-700 font-bold hover:underline px-1.5"
                    title="Show all latest dates"
                  >
                    Clear Date
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Search box */}
          <div className="relative mb-6">
            <Search className="absolute left-3.5 top-3 text-slate-400" size={16} />
            <input
              type="text"
              value={dailySearch}
              onChange={(e) => {
                setDailySearch(e.target.value);
                setDailyPage(1);
              }}
              placeholder="Search daily current affairs by topic, scheme, or keyword..."
              className="w-full pl-10 pr-4 py-2.5 text-xs bg-slate-50 focus:bg-white rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-red-900/20 focus:border-red-900 transition-all"
            />
          </div>

          {isLoadingDaily ? (
            <div className="py-12 text-center text-slate-500 text-xs flex flex-col items-center justify-center gap-2">
              <RefreshCw className="animate-spin text-red-800" size={24} />
              <span>Fetching latest daily current affairs...</span>
            </div>
          ) : currentDaily.length === 0 ? (
            <div className="py-12 text-center bg-slate-50/60 rounded-xl border border-dashed border-slate-200">
              <Calendar className="mx-auto text-slate-400 mb-2" size={32} />
              <p className="text-slate-700 font-bold text-sm">No Current Affairs Entries Found</p>
              <p className="text-slate-500 text-xs mt-1">
                {selectedDate
                  ? `No published articles on ${new Date(selectedDate).toLocaleDateString("en-IN")}. Try clearing the date filter.`
                  : "Check back shortly or click Sync Updates to refresh from the database."}
              </p>
              {selectedDate && (
                <button
                  onClick={() => setSelectedDate("")}
                  className="mt-3 text-xs font-bold text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg border border-red-200 transition-colors"
                >
                  View All Latest Uploads
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {paginate(currentDaily, dailyPage).map((item) => {
                  const displayDate = item.publicationDate || item.date || item.createdAt;
                  const fileUrl = item.pdfUrl || item.fileUrl;
                  return (
                    <div
                      key={item._id || item.id || item.title}
                      className="p-5 bg-white hover:bg-slate-50/60 border border-slate-200 hover:border-red-200 rounded-xl transition-all shadow-xs hover:shadow-md flex flex-col md:flex-row justify-between items-start md:items-center gap-4 group"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          {item.category && (
                            <span className="text-[10px] font-black uppercase tracking-wider text-red-800 bg-red-50 border border-red-100 px-2 py-0.5 rounded">
                              {item.category}
                            </span>
                          )}
                          <span className="text-[11px] text-slate-500 flex items-center gap-1 font-medium">
                            <Calendar size={13} className="text-slate-400" />
                            {displayDate ? new Date(displayDate).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            }) : "Today"}
                          </span>
                        </div>

                        <h3 className="font-bold text-slate-900 group-hover:text-red-900 text-base transition-colors leading-snug">
                          {item.title}
                        </h3>

                        {item.description && (
                          <p className="text-slate-600 text-xs mt-1.5 leading-relaxed line-clamp-2">
                            {item.description}
                          </p>
                        )}
                      </div>

                      {fileUrl && (
                        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                          <button
                            onClick={() => handleOpenFile(fileUrl)}
                            className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 bg-red-800 hover:bg-red-900 text-white text-xs font-bold px-4 py-2.5 rounded-lg transition-all shadow-xs hover:shadow-md"
                          >
                            <ExternalLink size={14} />
                            <span>View / Download PDF</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {totalDailyPages > 1 && (
                <div className="flex justify-center mt-6 space-x-1.5">
                  {Array.from({ length: totalDailyPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setDailyPage(i + 1)}
                      className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                        dailyPage === i + 1
                          ? "bg-red-800 text-white shadow-xs"
                          : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* ================= 2. MONTHLY CURRENT AFFAIRS SECTION ================= */}
        <div id="monthly" className="scroll-mt-28 bg-white rounded-2xl shadow-md p-6 sm:p-8 border-t-4 border-blue-900">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 pb-5 border-b border-slate-100">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-blue-900">
                  Monthly Current Affairs Compendiums
                </h2>
                <span className="text-xs font-bold bg-blue-50 text-blue-800 px-2.5 py-0.5 rounded-full border border-blue-200">
                  {currentMonthly.length} {currentMonthly.length === 1 ? "Issue" : "Issues"}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Consolidated monthly magazines organized month-wise and year-wise for comprehensive revision.
              </p>
            </div>

            {/* Month & Year Selectors */}
            <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
              <select
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setMonthlyPage(1);
                }}
                className="text-xs border border-slate-200 bg-slate-50 hover:bg-white rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900"
              >
                <option value="All">All Months</option>
                {[
                  "January", "February", "March", "April", "May", "June",
                  "July", "August", "September", "October", "November", "December",
                ].map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>

              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setMonthlyPage(1);
                }}
                className="text-xs border border-slate-200 bg-slate-50 hover:bg-white rounded-lg px-3 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900"
              >
                <option value="All">All Years</option>
                {[2026, 2025, 2024, 2023].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Search box for monthly compendiums */}
          <div className="relative mb-6">
            <Search className="absolute left-3.5 top-3 text-slate-400" size={16} />
            <input
              type="text"
              value={monthlySearch}
              onChange={(e) => {
                setMonthlySearch(e.target.value);
                setMonthlyPage(1);
              }}
              placeholder="Search monthly digests by topic or month name..."
              className="w-full pl-10 pr-4 py-2.5 text-xs bg-slate-50 focus:bg-white rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900 transition-all"
            />
          </div>

          {isLoadingMonthly ? (
            <div className="py-12 text-center text-slate-500 text-xs flex flex-col items-center justify-center gap-2">
              <RefreshCw className="animate-spin text-blue-900" size={24} />
              <span>Fetching published monthly compendiums...</span>
            </div>
          ) : currentMonthly.length === 0 ? (
            <div className="py-12 text-center bg-slate-50/60 rounded-xl border border-dashed border-slate-200">
              <Calendar className="mx-auto text-slate-400 mb-2" size={32} />
              <p className="text-slate-700 font-bold text-sm">No Monthly Compendiums Found</p>
              <p className="text-slate-500 text-xs mt-1">
                No compendium matches {selectedMonth !== "All" ? selectedMonth : ""} {selectedYear !== "All" ? selectedYear : ""}.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {paginate(currentMonthly, monthlyPage).map((item) => {
                  const fileUrl = item.pdfUrl || item.fileUrl;
                  return (
                    <div
                      key={item._id || item.id || item.title}
                      className="p-5 bg-white hover:bg-slate-50/60 border border-slate-200 hover:border-blue-200 rounded-xl transition-all shadow-xs hover:shadow-md flex flex-col justify-between group"
                    >
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-blue-800 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-full">
                            {item.month} {item.year}
                          </span>
                          <span className="text-[10px] text-slate-400 font-medium">
                            Monthly Digest
                          </span>
                        </div>

                        <h3 className="font-bold text-slate-900 group-hover:text-blue-900 text-base transition-colors leading-snug">
                          {item.title}
                        </h3>

                        {item.description && (
                          <p className="text-slate-600 text-xs mt-2 leading-relaxed line-clamp-2">
                            {item.description}
                          </p>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
                        <span className="text-[11px] text-slate-400">
                          {item.pdfOriginalName || "Full UPSC Edition"}
                        </span>

                        {fileUrl ? (
                          <button
                            onClick={() => handleOpenFile(fileUrl)}
                            className="inline-flex items-center gap-1.5 bg-blue-900 hover:bg-blue-800 text-white text-xs font-bold px-3.5 py-2 rounded-lg transition-all shadow-xs"
                          >
                            <Download size={13} />
                            <span>Download PDF</span>
                          </button>
                        ) : (
                          <span className="text-[11px] text-slate-400 italic">PDF Coming Soon</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalMonthlyPages > 1 && (
                <div className="flex justify-center mt-6 space-x-1.5">
                  {Array.from({ length: totalMonthlyPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setMonthlyPage(i + 1)}
                      className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                        monthlyPage === i + 1
                          ? "bg-blue-900 text-white shadow-xs"
                          : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
